/**
 * Flex CIS — pure deterministic РФ budget pricing engine.
 *
 * Computes a Georgia↔Russia (CIS corridor) shipment's GEL budget bottom-up:
 * place → recipient → order. Fully deterministic and side-effect free — no DB,
 * no HTTP, no zod, no СДЭК calls, no clock/random. Inputs are plain TS objects;
 * a later slice maps DB rows / СДЭК results into them.
 *
 * This is corridor "cis". The existing Europe engine (`computeFlexQuote`,
 * `flexRoundGelFinal`) is NOT reused and NOT modified — CIS is stepped,
 * per-place, and rounds the final total DOWN to 10.
 *
 * Currency: lari (GEL), carried as `number` with tetri precision (2 decimals).
 */
// ---------------------------------------------------------------------------
// Money constants (single source of truth — each covered by a contract test).
// ---------------------------------------------------------------------------
/** Personal base for the first kilogram. */
export const FLEX_CIS_PERSONAL_BASE_FIRST_KG_GEL = 30;
/** Personal surcharge per extra (ceil) kilogram beyond the first. */
export const FLEX_CIS_PERSONAL_PER_EXTRA_KG_GEL = 15;
/** Floor applied to the recipient's priceable-personal subtotal. */
export const FLEX_CIS_PERSONAL_FLOOR_GEL = 45;
/** Documents base per document. */
export const FLEX_CIS_DOCUMENT_PER_DOC_GEL = 68;
/** Medicine flat base (covers up to FLEX_CIS_MEDICINE_FREE_PACKS packs). */
export const FLEX_CIS_MEDICINE_BASE_GEL = 68;
/** Number of medicine packs included in the flat base. */
export const FLEX_CIS_MEDICINE_FREE_PACKS = 7;
/** Medicine surcharge per pack beyond the free allowance. */
export const FLEX_CIS_MEDICINE_PER_EXTRA_PACK_GEL = 10;
/** Alcohol price per litre. */
export const FLEX_CIS_ALCOHOL_PER_LITRE_GEL = 25;
/** Alcohol minimum base regardless of litres. */
export const FLEX_CIS_ALCOHOL_MIN_GEL = 50;
/**
 * Cheap-mode personal tariff per (ceil) kilogram, when the client self-shipped
 * via СДЭК to Vladikavkaz (RU-only budget mode). Distinct business rule from
 * `FLEX_CIS_PERSONAL_PER_EXTRA_KG_GEL`; the equal value is coincidental.
 */
export const FLEX_CIS_CHEAP_PERSONAL_PER_KG_GEL = 15;
/**
 * Cheap-mode floor on the recipient's priceable-personal subtotal (RU-only
 * budget mode). Distinct business rule from `FLEX_CIS_PERSONAL_BASE_FIRST_KG_GEL`;
 * the equal value is coincidental.
 */
export const FLEX_CIS_CHEAP_PERSONAL_MIN_GEL = 30;
/** Declared-value step size for the per-place price surcharge. */
export const FLEX_CIS_PRICE_SURCHARGE_STEP_GEL = 500;
/** Price surcharge added per completed declared-value step. */
export const FLEX_CIS_PRICE_SURCHARGE_PER_STEP_GEL = 5;
/** Surcharge added per recipient when delivery is to the door. */
export const FLEX_CIS_DOOR_SURCHARGE_GEL = 10;
/** Share of our cost absorbed before charging remoteness (17%). */
export const FLEX_CIS_REMOTENESS_ABSORB_RATE = 0.17;
/** Pickup fee charged per unit of FLEX_CIS_PICKUP_PLACES_PER_UNIT places. */
export const FLEX_CIS_PICKUP_PER_8_PLACES_GEL = 50;
/** Number of physical places covered by one pickup-fee unit. */
export const FLEX_CIS_PICKUP_PLACES_PER_UNIT = 8;
/** Surcharge for a high sender floor without an elevator. */
export const FLEX_CIS_NO_ELEVATOR_SURCHARGE_GEL = 50;
/** Sender-floor threshold above which the no-elevator surcharge applies. */
export const FLEX_CIS_NO_ELEVATOR_FLOOR_THRESHOLD = 3;
/** Receiver-payment rate applied to the floored total (5%). RU only. */
export const FLEX_CIS_RECEIVER_PAYMENT_RATE = 0.05;
/** Final total is rounded DOWN to a multiple of this. */
export const FLEX_CIS_TOTAL_ROUND_DOWN_GEL = 10;
// ---------------------------------------------------------------------------
// BY/KZ destination-country constants (single source of truth — contract test).
// ---------------------------------------------------------------------------
/** BY personal base covering the first FLEX_CIS_BYKZ_PERSONAL_INCLUDED_KG kg. */
export const FLEX_CIS_BY_PERSONAL_BASE_GEL = 55;
/** BY personal surcharge per extra (ceil) kilogram beyond the included weight. */
export const FLEX_CIS_BY_PERSONAL_PER_EXTRA_KG_GEL = 17;
/** KZ personal base covering the first FLEX_CIS_BYKZ_PERSONAL_INCLUDED_KG kg. */
export const FLEX_CIS_KZ_PERSONAL_BASE_GEL = 65;
/** KZ personal surcharge per extra (ceil) kilogram beyond the included weight. */
export const FLEX_CIS_KZ_PERSONAL_PER_EXTRA_KG_GEL = 20;
/** Kilograms covered by the BY/KZ personal base before per-extra-kg kicks in. */
export const FLEX_CIS_BYKZ_PERSONAL_INCLUDED_KG = 2;
/** BY/KZ documents base per document. */
export const FLEX_CIS_BYKZ_DOCUMENT_PER_DOC_GEL = 78;
/** BY alcohol base covering the first FLEX_CIS_BY_ALCOHOL_INCLUDED_LITRES litres. */
export const FLEX_CIS_BY_ALCOHOL_BASE_GEL = 60;
/** BY alcohol surcharge per extra (ceil) litre beyond the included litres. */
export const FLEX_CIS_BY_ALCOHOL_PER_EXTRA_LITRE_GEL = 30;
/** Litres covered by the BY alcohol base before per-extra-litre kicks in. */
export const FLEX_CIS_BY_ALCOHOL_INCLUDED_LITRES = 2;
/**
 * Single source of truth for the CIS place categories, in canonical order.
 * The Prisma enum `FlexCisPlaceCategory` mirrors this list exactly — enforced
 * by `flexCisCategory.alignment.test.ts`. Frozen so it cannot drift at runtime.
 */
export const FLEX_CIS_CATEGORIES = Object.freeze([
    "personal",
    "documents",
    "medicine",
    "alcohol",
    "appliances",
    "digital",
]);
/**
 * Russian display labels for the CIS place categories — single source of truth
 * (architecture rule №1). Imported by the AMO-CIS опись builder
 * (apps/api/src/services/flexCisOrderAmo.service.ts) instead of hardcoding
 * labels in the API. Every `FLEX_CIS_CATEGORIES` member MUST have an entry —
 * enforced by `flexCisCategoryLabels.alignment.test.ts`.
 */
export const FLEX_CIS_CATEGORY_LABELS_RU = {
    personal: "Личные вещи",
    documents: "Документы",
    medicine: "Лекарства",
    alcohol: "Алкоголь",
    appliances: "Техника",
    digital: "Цифровая техника",
};
// ---------------------------------------------------------------------------
// Helpers.
// ---------------------------------------------------------------------------
/** Round a monetary amount to tetri precision (2 decimals). */
function round2(amountGel) {
    return Math.round(amountGel * 100) / 100;
}
/** Whether a value is a finite number that is strictly greater than zero. */
function isFinitePositive(value) {
    return typeof value === "number" && Number.isFinite(value) && value > 0;
}
/** Whether a value is an integer that is at least one. */
function isIntAtLeastOne(value) {
    return typeof value === "number" && Number.isInteger(value) && value >= 1;
}
/** Whether a value is a finite number that is at least zero. */
function isFiniteNonNegative(value) {
    return typeof value === "number" && Number.isFinite(value) && value >= 0;
}
/**
 * Round the final order total DOWN to the nearest multiple of
 * `FLEX_CIS_TOTAL_ROUND_DOWN_GEL`. Distinct from Europe's `flexRoundGelFinal`,
 * which rounds UP to 5.
 */
export function flexCisRoundFloor10(amountGel) {
    return Math.floor(amountGel / FLEX_CIS_TOTAL_ROUND_DOWN_GEL) * FLEX_CIS_TOTAL_ROUND_DOWN_GEL;
}
/**
 * Personal base for a billed (ceil) kilogram count, country-branched. In cheap
 * mode (RU-only) the standard RU tariff is replaced by a flat per-kg rate.
 */
function personalBase(country, billedKg, cheapMode) {
    if (cheapMode) {
        // Cheap mode is RU-only (gated by the caller); flat 15₾ per billed kg.
        return FLEX_CIS_CHEAP_PERSONAL_PER_KG_GEL * billedKg;
    }
    switch (country) {
        case "BY":
            return billedKg <= FLEX_CIS_BYKZ_PERSONAL_INCLUDED_KG
                ? FLEX_CIS_BY_PERSONAL_BASE_GEL
                : FLEX_CIS_BY_PERSONAL_BASE_GEL +
                    FLEX_CIS_BY_PERSONAL_PER_EXTRA_KG_GEL * (billedKg - FLEX_CIS_BYKZ_PERSONAL_INCLUDED_KG);
        case "KZ":
            return billedKg <= FLEX_CIS_BYKZ_PERSONAL_INCLUDED_KG
                ? FLEX_CIS_KZ_PERSONAL_BASE_GEL
                : FLEX_CIS_KZ_PERSONAL_BASE_GEL +
                    FLEX_CIS_KZ_PERSONAL_PER_EXTRA_KG_GEL * (billedKg - FLEX_CIS_BYKZ_PERSONAL_INCLUDED_KG);
        case "RU":
            return (FLEX_CIS_PERSONAL_BASE_FIRST_KG_GEL + FLEX_CIS_PERSONAL_PER_EXTRA_KG_GEL * (billedKg - 1));
    }
}
/**
 * Compute the category base tariff `base(m)` for a place under the given
 * destination country. RU branches are unchanged; BY/KZ add their own bases and
 * mark `appliances` (BY/KZ) and `alcohol` (KZ) unpriceable by policy.
 */
function computeBase(place, country, cheapMode) {
    switch (place.category) {
        case "personal": {
            if (!isFinitePositive(place.weightKg)) {
                return { base: null, invalidQuantity: true, policyFlag: null };
            }
            return {
                base: personalBase(country, Math.ceil(place.weightKg), cheapMode),
                invalidQuantity: false,
                policyFlag: null,
            };
        }
        case "documents": {
            if (!isIntAtLeastOne(place.documentsCount)) {
                return { base: null, invalidQuantity: true, policyFlag: null };
            }
            const perDoc = country === "RU" ? FLEX_CIS_DOCUMENT_PER_DOC_GEL : FLEX_CIS_BYKZ_DOCUMENT_PER_DOC_GEL;
            return {
                base: perDoc * place.documentsCount,
                invalidQuantity: false,
                policyFlag: null,
            };
        }
        case "medicine": {
            if (!isIntAtLeastOne(place.packsCount)) {
                return { base: null, invalidQuantity: true, policyFlag: null };
            }
            const base = place.packsCount <= FLEX_CIS_MEDICINE_FREE_PACKS
                ? FLEX_CIS_MEDICINE_BASE_GEL
                : FLEX_CIS_MEDICINE_BASE_GEL +
                    FLEX_CIS_MEDICINE_PER_EXTRA_PACK_GEL *
                        (place.packsCount - FLEX_CIS_MEDICINE_FREE_PACKS);
            return { base, invalidQuantity: false, policyFlag: null };
        }
        case "alcohol": {
            if (country === "KZ") {
                // Prohibited — unpriceable by policy, no quantity validation.
                return {
                    base: null,
                    invalidQuantity: false,
                    policyFlag: "kz_alcohol_prohibited",
                };
            }
            if (!isFinitePositive(place.litres)) {
                return { base: null, invalidQuantity: true, policyFlag: null };
            }
            if (country === "BY") {
                const billedL = Math.ceil(place.litres);
                const base = billedL <= FLEX_CIS_BY_ALCOHOL_INCLUDED_LITRES
                    ? FLEX_CIS_BY_ALCOHOL_BASE_GEL
                    : FLEX_CIS_BY_ALCOHOL_BASE_GEL +
                        FLEX_CIS_BY_ALCOHOL_PER_EXTRA_LITRE_GEL *
                            (billedL - FLEX_CIS_BY_ALCOHOL_INCLUDED_LITRES);
                return { base, invalidQuantity: false, policyFlag: null };
            }
            return {
                base: Math.max(FLEX_CIS_ALCOHOL_MIN_GEL, FLEX_CIS_ALCOHOL_PER_LITRE_GEL * place.litres),
                invalidQuantity: false,
                policyFlag: null,
            };
        }
        case "appliances": {
            // Admin sets the price manually in EVERY country and mode — unpriceable
            // by policy, no quantity validation (mirrors digital, shares the
            // "manual_pricing" flag).
            return {
                base: null,
                invalidQuantity: false,
                policyFlag: "manual_pricing",
            };
        }
        case "digital": {
            // Admin sets the price manually — always unpriceable, but not an
            // invalid quantity (carries the "manual_pricing" flag instead).
            return {
                base: null,
                invalidQuantity: false,
                policyFlag: "manual_pricing",
            };
        }
    }
}
/** The per-place declared-value price surcharge `надбавка_цены(m)`. */
function computePriceSurcharge(declaredValueGel) {
    return (Math.floor(declaredValueGel / FLEX_CIS_PRICE_SURCHARGE_STEP_GEL) *
        FLEX_CIS_PRICE_SURCHARGE_PER_STEP_GEL);
}
/**
 * Price one place under the given destination country. Priceable places get
 * numeric `baseGel`, `priceSurchargeGel` and `budgetGel = round2(base +
 * surcharge)`. Unpriceable places get all-null monetary fields and a reason
 * flag (`"manual_pricing"` for digital and BY/KZ appliances,
 * `"kz_alcohol_prohibited"` for KZ alcohol, `"invalid_quantity"`,
 * `"invalid_declared_value"`).
 */
function priceOnePlace(place, country, cheapMode) {
    const flags = [];
    const { base, invalidQuantity, policyFlag } = computeBase(place, country, cheapMode);
    if (invalidQuantity) {
        flags.push("invalid_quantity");
    }
    if (policyFlag !== null) {
        flags.push(policyFlag);
    }
    const declaredValid = isFiniteNonNegative(place.declaredValueGel);
    if (!declaredValid) {
        flags.push("invalid_declared_value");
    }
    const priceable = base !== null && declaredValid;
    if (!priceable) {
        return {
            category: place.category,
            baseGel: null,
            priceSurchargeGel: null,
            budgetGel: null,
            flags,
        };
    }
    const priceSurcharge = computePriceSurcharge(place.declaredValueGel);
    return {
        category: place.category,
        baseGel: round2(base),
        priceSurchargeGel: round2(priceSurcharge),
        budgetGel: round2(base + priceSurcharge),
        flags,
    };
}
// ---------------------------------------------------------------------------
// Recipient pricing (B3).
// ---------------------------------------------------------------------------
function priceRecipient(recipient, country, cheapMode) {
    const places = recipient.places.map((place) => priceOnePlace(place, country, cheapMode));
    const flags = [];
    // Split priceable budgets into personal vs other.
    let personalSum = 0;
    let hasPriceablePersonal = false;
    let otherSum = 0;
    recipient.places.forEach((place, index) => {
        const result = places[index];
        if (result === undefined || result.budgetGel === null) {
            return;
        }
        if (place.category === "personal") {
            hasPriceablePersonal = true;
            personalSum += result.budgetGel;
        }
        else {
            otherSum += result.budgetGel;
        }
    });
    // Personal floor on the recipient's priceable-personal subtotal — RU only.
    // Normal RU uses 45₾; cheap mode (RU-only) uses 30₾ (exact analogue, lower
    // floor). BY/KZ minimal personal bases (55/65) already exceed 45, so the
    // floor can never alter a BY/KZ result; it is skipped to keep the gating
    // explicit and doctrine-correct.
    const personalFloorGel = cheapMode
        ? FLEX_CIS_CHEAP_PERSONAL_MIN_GEL
        : FLEX_CIS_PERSONAL_FLOOR_GEL;
    const personalPart = !hasPriceablePersonal
        ? 0
        : country === "RU"
            ? Math.max(personalFloorGel, personalSum)
            : personalSum;
    // Door surcharge +10 — RU only. BY/KZ accept "door" but add nothing.
    const door = country === "RU" && recipient.deliveryType === "door" ? FLEX_CIS_DOOR_SURCHARGE_GEL : 0;
    const ourCostGel = round2(personalPart + otherSum + door);
    // Remoteness (17% rule) — RU normal mode only. BY/KZ and cheap mode ignore
    // cdekGel entirely and never emit "manual_remoteness" (cheap mode mirrors the
    // BY/KZ remoteness gating exactly: the 17% rule is switched off).
    let remotenessGel;
    if (country !== "RU" || cheapMode) {
        remotenessGel = 0;
    }
    else if (isFiniteNonNegative(recipient.cdekGel ?? undefined)) {
        const cdekGel = recipient.cdekGel;
        remotenessGel = round2(Math.max(0, cdekGel - FLEX_CIS_REMOTENESS_ABSORB_RATE * ourCostGel));
    }
    else {
        remotenessGel = 0;
        flags.push("manual_remoteness");
    }
    const budgetGel = round2(ourCostGel + remotenessGel);
    return {
        ourCostGel,
        remotenessGel,
        budgetGel,
        flags,
        places,
    };
}
// ---------------------------------------------------------------------------
// Order total (B4) + partial-pricing flags (B5).
// ---------------------------------------------------------------------------
/**
 * Compute the full Flex CIS quote: per-place budgets, per-recipient budgets,
 * and the order total. Always returns numbers — never throws on per-place
 * unpriceability. Unpriceable places and null `cdekGel` are surfaced through
 * flags and `manualPricingRequired`.
 */
export function computeFlexCisQuote(input) {
    const country = input.destinationCountry ?? "RU";
    // Cheap mode (client self-shipped via СДЭК) is RU-only and activated only by
    // the exact value `true`. For BY/KZ the flag is ignored entirely (no mode
    // change, no flag) — same gating doctrine as BY/KZ ignoring cdekGel.
    const cheapMode = country === "RU" && input.clientSelfShippedCdek === true;
    const recipients = input.recipients.map((recipient) => priceRecipient(recipient, country, cheapMode));
    const totalPlaces = input.recipients.reduce((sum, recipient) => sum + recipient.places.length, 0);
    const pickupFeeGel = input.pickupEnabled
        ? Math.ceil(totalPlaces / FLEX_CIS_PICKUP_PLACES_PER_UNIT) * FLEX_CIS_PICKUP_PER_8_PLACES_GEL
        : 0;
    const noElevatorSurchargeGel = input.senderFloor != null &&
        input.senderFloor > FLEX_CIS_NO_ELEVATOR_FLOOR_THRESHOLD &&
        input.senderHasElevator === false
        ? FLEX_CIS_NO_ELEVATOR_SURCHARGE_GEL
        : 0;
    const recipientsBudgetSum = recipients.reduce((sum, recipient) => sum + recipient.budgetGel, 0);
    // round2 before flooring: the FP sum of tetri-precision budgets can dip a
    // hair below an exact multiple of 10 (e.g. 89.99999999999999), and flooring
    // the raw sum would then eat a whole 10-GEL step.
    const rawTotal = round2(recipientsBudgetSum + pickupFeeGel + noElevatorSurchargeGel);
    const totalGel = flexCisRoundFloor10(rawTotal);
    // 5% receiver payment — RU only; BY/KZ have no receiver payment.
    const receiverPaymentGel = country === "RU" ? round2(totalGel * FLEX_CIS_RECEIVER_PAYMENT_RATE) : 0;
    // Partial pricing: manual pricing is required when any place is unpriceable
    // or any recipient lacks a usable cdekGel (carries "manual_remoteness").
    const anyUnpriceablePlace = recipients.some((recipient) => recipient.places.some((place) => place.budgetGel === null));
    const anyManualRemoteness = recipients.some((recipient) => recipient.flags.includes("manual_remoteness"));
    const manualPricingRequired = anyUnpriceablePlace || anyManualRemoteness;
    const flags = [];
    if (manualPricingRequired) {
        flags.push("manual_pricing_required");
    }
    return {
        totalGel,
        receiverPaymentGel,
        pickupFeeGel,
        noElevatorSurchargeGel,
        manualPricingRequired,
        flags,
        recipients,
    };
}
//# sourceMappingURL=pricing.js.map